import { ObjectLibraryTreeNode } from '@/model/GraphicModel';
import { SortAscendingOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, List, Radio, RadioChangeEvent, Space, Switch } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { KeyboardEvent, useState } from 'react';
import { batch } from 'react-redux';
import { SortDirections, SorterConfig } from '../../../../../../model/CommonModel';
import { ObjectLibraryFolder, ObjectTypes } from '../../../../../../model/DeviceAdminModel';
import { SearchType, ShowOptions } from '../../../../../../model/RolesModel';
import { Divider, Popover } from '../../../../../common';
import { buildRadioOptionsByEnum } from '../../helper';
import styles from './searchHardware.module.scss';

type Props = {
	onSearchApplied: (params: SearchType, isFilterApplied: boolean) => void;
};

enum SortField {
	Name,
	Address,
}

const { Group: RadioGroup } = Radio;

type SearchOption = {
	label: string;
	value: ObjectTypes;
	index: number;
};

const searchOptions: SearchOption[] = [
	{ label: _('Port'), value: ObjectTypes.pollingPorts, index: 0 },
	{ label: _('Xbox'), value: ObjectTypes.xboxes, index: 1 },
	{ label: _('Controller'), value: ObjectTypes.hardwareControllers, index: 2 },
	{ label: _('Doors'), value: ObjectTypes.doors, index: 3 },
	{ label: _('Readers'), value: ObjectTypes.readers, index: 3 },
	{ label: _('Relays'), value: ObjectTypes.relays, index: 3 },
	{ label: _('ExpansionRelays'), value: ObjectTypes.expansionRelays, index: 3 },
	{ label: _('Inputs'), value: ObjectTypes.inputs, index: 3 },
	{ label: _('ExpansionInputs'), value: ObjectTypes.expansionInputs, index: 3 },
];
const lastIndex = 3;
const defaultObjectTypes: ObjectTypes[] = [
	ObjectTypes.servers,
	ObjectTypes.pollingPorts,
	ObjectTypes.xboxes,
	ObjectTypes.hardwareControllers,
	ObjectTypes.inputs,
	ObjectTypes.expansionInputs,
	ObjectTypes.relays,
	ObjectTypes.expansionRelays,
	ObjectTypes.readers,
	ObjectTypes.doors,
];
export const isDeepChildren = (objectType: ObjectTypes, node?: ObjectLibraryTreeNode): boolean => {
	if (node && node.folderType === ObjectLibraryFolder.FOLDER) {
		return false;
	}
	const found = searchOptions.find(x => x.value === objectType);
	return found.index === lastIndex;
};
export const defaultSearchType: SearchType = {
	showOptions: ShowOptions.ShowAll,
	rootObjectTypes: [ObjectTypes.pollingPorts],
	selectedHardwareOptions: [...defaultObjectTypes],
	selectedSearchByHardwareTypes: [...defaultObjectTypes],
	sortConfig: {
		direction: SortDirections.Ascend,
		field: SortField[SortField.Name],
	},
};

const SearchHardware: React.FC<Props> = ({ onSearchApplied }) => {
	//#region state management variables
	const [searchText, setSearchText] = useState<string>('');
	const [isFilterActive, setIsFilterActive] = useState<boolean>(false);
	const [isShowOptionActive, setIsShowOptionActive] = useState<boolean>(false);
	const [isSortByActive, setIsSortByActive] = useState<boolean>(false);
	const [selectedSearchByHardwareTypes, setSelectedSearchByHardwareTypes] = useState<SearchOption[]>([...searchOptions]);
	const [selectedHardwareTypes, setSelectedHardwareTypes] = useState<SearchOption[]>([...searchOptions]);
	const [showOption, setShowOption] = useState<ShowOptions>(defaultSearchType.showOptions);
	const [sortConfig, setSortConfig] = useState<SorterConfig>(defaultSearchType.sortConfig);
	//#endregion

	const onSearch = () => {
		const searchType: SearchType = {
			searchText,
			isDeepChildren: false,
			rootObjectTypes: [],
			selectedHardwareOptions: selectedHardwareTypes.map(x => x.value),
			selectedSearchByHardwareTypes: [],
			showOptions: showOption,
			sortConfig: sortConfig,
		};
		selectedHardwareTypes.forEach(selectedHardwareType => {
			if (selectedSearchByHardwareTypes.some(item => item.value === selectedHardwareType.value)) {
				searchType.selectedSearchByHardwareTypes.push(selectedHardwareType.value);
			}
		});
		searchType.selectedHardwareOptions.push(ObjectTypes.servers);
		searchType.selectedSearchByHardwareTypes.push(ObjectTypes.servers);
		if (searchType.selectedHardwareOptions.length) {
			const firstElement: SearchOption = selectedHardwareTypes[0];
			searchType.isDeepChildren = isDeepChildren(firstElement.value);
			searchType.rootObjectTypes = searchOptions
				.filter(x => x.index === firstElement.index && searchType.selectedHardwareOptions.some(y => y === x.value))
				.map(x => x.value);
		}
		onSearchApplied(searchType, isFilterActive || isShowOptionActive || isSortByActive || searchText?.length > 0);
	};

	const onResetFilter = () => {
		const searchType: SearchType = {
			...defaultSearchType,
		};
		batch(() => {
			setIsFilterActive(false);
			setIsShowOptionActive(false);
			setIsSortByActive(false);
			setSelectedSearchByHardwareTypes([...searchOptions]);
			setSelectedHardwareTypes([...searchOptions]);
			setShowOption(searchType.showOptions);
			setSearchText(searchType.searchText);
			setSortConfig(searchType.sortConfig);
		});
		onSearchApplied(searchType, false);
	};

	const onSearchOptionCheckboxChange = (checked: boolean, searchOption: SearchOption) => {
		let cloneState: SearchOption[] = [];
		if (!checked) {
			cloneState = selectedSearchByHardwareTypes.filter(item => item.value !== searchOption.value);
		} else {
			cloneState = [...selectedSearchByHardwareTypes, searchOption];
		}
		batch(() => {
			updateFilterState(selectedHardwareTypes.length, cloneState.length);
			setSelectedSearchByHardwareTypes(cloneState.sort((a, b) => a.value - b.value));
		});
	};

	const onSearchOptionSwitchChange = (checked: boolean, searchOption: SearchOption) => {
		let cloneState: SearchOption[] = [];
		if (!checked) {
			cloneState = selectedHardwareTypes.filter(item => item.value !== searchOption.value);
		} else {
			cloneState = [...selectedHardwareTypes, searchOption];
		}

		batch(() => {
			updateFilterState(cloneState.length, selectedSearchByHardwareTypes.length);
			setSelectedHardwareTypes(cloneState.sort((a, b) => a.index - b.index));
		});
	};

	const updateFilterState = (switchCount: number, checkboxCount: number) => {
		const originalOptionsCount: number = searchOptions.length;
		setIsFilterActive(switchCount !== originalOptionsCount || checkboxCount !== originalOptionsCount);
	};

	const handleOnChangeShowView = (e: RadioChangeEvent) => {
		const newShowOption: ShowOptions = e.target.value;
		batch(() => {
			setShowOption(newShowOption);
			setIsShowOptionActive(newShowOption !== defaultSearchType.showOptions);
		});
	};

	const handleOnChangeSortField = (e: RadioChangeEvent) => {
		const field: string = e.target.value;
		setSortConfig(prevState => ({ ...prevState, field: e.target.value }));
		updateSortState({
			...sortConfig,
			field,
		});
	};

	const handleOnChangeSortDirection = (e: RadioChangeEvent) => {
		const direction: SortDirections = e.target.value;
		setSortConfig(prevState => ({ ...prevState, direction: direction }));
		updateSortState({
			...sortConfig,
			direction: direction,
		});
	};

	const updateSortState = (newSortState: SorterConfig) => {
		let isActiveSort: boolean = false;
		if (newSortState.field !== defaultSearchType.sortConfig.field || newSortState.direction !== defaultSearchType.sortConfig.direction) {
			isActiveSort = true;
		}
		setIsSortByActive(isActiveSort);
	};

	const handleOnKeyUpSearchInput = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			onSearch();
		}
	};

	return (
		<div className={styles.container}>
			<Popover title={`${_('Filters')}:`} isActive={isFilterActive}>
				<div className={styles.containerPopOver}>
					<div className={styles.itemHeaderContainer}>
						<label>{_('SearchIn')}</label>
						<label>{_('ShowDevice')}</label>
					</div>
					<List
						size='small'
						dataSource={searchOptions}
						renderItem={searchOption => (
							<div key={searchOption.value} className={styles.itemContainer}>
								<Checkbox
									key={`checkbox-${searchOption}`}
									checked={selectedSearchByHardwareTypes.some(item => item.value === searchOption.value)}
									onChange={(e: CheckboxChangeEvent) => onSearchOptionCheckboxChange(e.target.checked, searchOption)}
									disabled={!selectedHardwareTypes.some(item => item === searchOption)}>
									<span>{searchOption.label}</span>
								</Checkbox>
								<Switch
									key={`switch-${searchOption}`}
									checked={selectedHardwareTypes.some(item => item === searchOption)}
									onChange={(e: boolean) => onSearchOptionSwitchChange(e, searchOption)}
								/>
							</div>
						)}
					/>
				</div>
			</Popover>
			<Input
				allowClear
				className={styles.searchBar}
				maxLength={64}
				placeholder={_('SearchByName')}
				onChange={e => {
					setSearchText(e.target.value);
				}}
				onKeyUp={handleOnKeyUpSearchInput}
				value={searchText}
				disabled={false}
			/>
			<Popover title={_(ShowOptions[showOption])} isActive={isShowOptionActive} buttonClassName={styles.showOptionsButton}>
				<RadioGroup onChange={handleOnChangeShowView} value={showOption}>
					{buildRadioOptionsByEnum('vertical', ShowOptions)}
				</RadioGroup>
			</Popover>
			<Popover
				title={`${_('SortBy')} ${_(sortConfig.field)}`}
				isActive={isSortByActive}
				buttonIcon={<SortAscendingOutlined />}
				buttonClassName={styles.sortByButton}>
				<RadioGroup onChange={handleOnChangeSortField} value={sortConfig.field}>
					<Space direction={'vertical'}>
						<Radio id={`radioOption-${SortField.Name}`} value={SortField[SortField.Name]}>
							{_('Name')}
						</Radio>
						<Radio id={`radioOption-${SortField.Address}`} value={SortField[SortField.Address]}>
							{_('Address')}
						</Radio>
					</Space>
				</RadioGroup>
				<Divider />
				<RadioGroup onChange={handleOnChangeSortDirection} value={sortConfig.direction}>
					{buildRadioOptionsByEnum('vertical', SortDirections, true)}
				</RadioGroup>
			</Popover>
			<div className={styles.resetButtons}>
				<Button onClick={onResetFilter}>{_('Reset')}</Button>
			</div>
			<Button type='primary' onClick={onSearch}>
				{_('Apply')}
			</Button>
		</div>
	);
};

export { SearchHardware };
